import React, { useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import classNames from "classnames";

// Actions
import { fetchLogin, setVirtualKeyboard } from "../../actions";

// MUI Components
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Card from "@material-ui/core/Card";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";

// Icons
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";

// Other
import { customTheme } from "../../constants/customTheme";
import { colors } from "../../constants/colors";
import Header from "../Header/Header";
import SnackbarInfo from "../Shared/Snackbar";
import VirtualKeyboard from "../Shared/VirtualKeyboard";

const useStyles = makeStyles({
  fill: {
    flex: "1 0 auto",
    backgroundColor: (props) => colors[props.theme].backgroundColor,
  },
  cardRoot: (props) => ({
    ...customTheme(props.theme).card.root,
    maxWidth: "450px",
  }),
  title: customTheme().card.title,
  buttonContainer: customTheme().card.buttonContainer,
  buttonProgress: (props) => customTheme(props.theme).buttonProgress,
  contentItem: customTheme().card.contentItem,
  copyright: customTheme().copyright,
  loginButton: (props) => customTheme(props.theme).buttonPrimary,
  rememberMeCheckbox: { margin: "10px 10px 10px 0px" },
});

const LoginPage = (props) => {
  const classes = useStyles(props);

  // State
  const [loginValues, setLoginValues] = React.useState({
    username: "",
    password: "",
  });
  const [rememberMe, setRememberMe] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [keyboardInput, setKeyboardInput] = React.useState({
    inputName: null,
    inputUpdate: null,
  });

  // Reset username and password textFields if the login attempt is unsuccessful
  useEffect(() => {
    if (props.loginFetching === false) {
      setLoginValues({ username: "", password: "" });
    }
  }, [props.loginFetching]);

  const handleFormChange = (input_value) => (event) => {
    if (event.target) {
      setLoginValues({ ...loginValues, [input_value]: event.target.value });
      // Update virtualKeyboard with the manually entered values
      setKeyboardInput({
        ...keyboardInput,
        inputUpdate: { ...loginValues, [input_value]: event.target.value },
      });
    } else {
      setLoginValues({ ...loginValues, [input_value]: event });
    }
  };

  const handleSubmit = () => {
    props.fetchLogin(loginValues, rememberMe);
    props.setVirtualKeyboard(false);
  };

  return (
    <Grid
      item
      container
      className={classNames(classes.fill, "noselect")}
      direction="column"
    >
      {/* HEADER */}
      <Header
      // eventSource={props.eventSource}
      />

      <Grid
        item
        container
        className={classes.fill}
        direction="column"
        justify="space-between"
      >
        <Grid
          item
          container
          className={classes.fill}
          direction="column"
          justify="center"
          alignItems="center"
        >
          <Grid item>
            {/* INFORMATION CARD */}
            <Card className={classes.cardRoot}>
              <Typography
                variant="h5"
                color="textPrimary"
                align="center"
                className={classes.title}
              >
                User Login
              </Typography>

              {/* USERNAME */}
              <TextField
                required
                className={classes.contentItem}
                label="Username"
                value={loginValues.username}
                onChange={handleFormChange("username")}
                onFocus={() => {
                  setKeyboardInput({ ...keyboardInput, inputName: "username" });
                  props.setVirtualKeyboard(true);
                }}
                onKeyPress={(event) => {
                  if (
                    event.key === "Enter" &&
                    loginValues.username.length !== 0 &&
                    loginValues.password.length !== 0
                  ) {
                    handleSubmit();
                  }
                }}
              />

              {/* PASSWORD */}
              <TextField
                required
                className={classes.contentItem}
                label="Password"
                type={showPassword ? "text" : "password"}
                value={loginValues.password}
                onChange={handleFormChange("password")}
                onFocus={() => {
                  setKeyboardInput({ ...keyboardInput, inputName: "password" });
                  props.setVirtualKeyboard(true);
                }}
                onKeyPress={(event) => {
                  if (
                    event.key === "Enter" &&
                    loginValues.username.length !== 0 &&
                    loginValues.password.length !== 0
                  ) {
                    handleSubmit();
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={(event) => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {/* REMEMBER ME */}
              <FormControlLabel
                key="rememberMeCheckbox"
                label="Remember Me"
                className={classes.rememberMeCheckbox}
                control={
                  <Checkbox
                    color="primary"
                    onChange={() => setRememberMe(!rememberMe)}
                    checked={rememberMe}
                    value={rememberMe}
                  />
                }
              />

              {/* BUTTON */}
              <div className={classes.buttonContainer}>
                <Button
                  className={classes.loginButton}
                  variant="contained"
                  color="primary"
                  onClick={() => handleSubmit()}
                  disabled={
                    //Disable if the information is not entered
                    loginValues.username.length === 0 ||
                    loginValues.password.length === 0 ||
                    props.loginFetching
                  }
                >
                  {props.loginFetching && (
                    <CircularProgress
                      size={25}
                      className={classes.buttonProgress}
                    />
                  )}
                  Login
                </Button>
              </div>
            </Card>
          </Grid>
        </Grid>

        {/* COPYRIGHT INFO */}
        <Grid item align="center" className={classes.copyright}>
          <Typography variant="caption" gutterBottom>
            This software is for investigational research use only. It is
            provided as-is with no warranty. &copy;{new Date().getFullYear()}
            MicroElastic Ultrasound Systems
          </Typography>
        </Grid>
      </Grid>

      {/* SNACKBAR FOR MESSAGE DISPLAY */}
      <SnackbarInfo />

      {/* VIRTUAL KEYBOARD */}
      <VirtualKeyboard
        anchor={"center"}
        keyboardInitialInput={loginValues}
        keyboardInputUpdate={keyboardInput.inputUpdate}
        keyboardInputName={keyboardInput.inputName}
        keyboardChange={handleFormChange(keyboardInput.inputName)}
        keyboardEnter={() => {
          if (
            loginValues.username.length !== 0 &&
            loginValues.password.length !== 0 &&
            !props.loginFetching
          ) {
            handleSubmit();
          }
        }}
      />
    </Grid>
  );
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      fetchLogin,
      setVirtualKeyboard,
    },
    dispatch
  );
};

const mapStateToProps = (state) => {
  return {
    loginFetching: state.userInfo.loginFetching,
    theme: state.utility.theme,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(LoginPage);
